import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardHeader,
  CardPreview,
  Text,
  Spinner,
  MessageBar,
  Textarea,
} from "@fluentui/react-components";
import { ArrowLeftRegular, Save24Regular } from "@fluentui/react-icons";
import {
  useCascoConditions,
  useUpdateInsurerCascoConditions,
} from "../../../state/queries/useCascoConditions";
import { InsurerCondition } from "../../../library/types/cascoConditions";
import { Navigation } from "../../../components/Navigation/Navigation";
import { ConfirmDialog } from "../../../components/ConfirmDialog";

export const CascoConditionEditPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { conditionId } = useParams<{ conditionId: string }>();
  const [editingInsurers, setEditingInsurers] = useState<{
    [key: number]: string;
  }>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
    useState(false);

  const { data: conditions, isLoading, error } = useCascoConditions();
  const updateInsurerMutation = useUpdateInsurerCascoConditions();

  const condition = conditions?.find(
    (c) => c.CascoConditionId === parseInt(conditionId || "0")
  );

  useEffect(() => {
    if (condition) {
      // Initialize editing state with current insurer texts
      const initialEditingState: { [key: number]: string } = {};
      condition.InsurerConditions.forEach((ic) => {
        initialEditingState[ic.CascoConditionInsurerId] = ic.InsurerText;
      });
      setEditingInsurers(initialEditingState);
    }
  }, [condition]);

  const handleInsurerTextChange = (
    cascoConditionInsurerId: number,
    newText: string
  ) => {
    setEditingInsurers((prev) => ({
      ...prev,
      [cascoConditionInsurerId]: newText,
    }));
    setHasChanges(true);
  };

  const handleSaveChanges = async () => {
    if (!condition) return;

    try {
      const updatedInsurerConditions: InsurerCondition[] =
        condition.InsurerConditions.map((ic) => ({
          CascoConditionInsurerId: ic.CascoConditionInsurerId,
          CascoConditionId: ic.CascoConditionId,
          InsurerName: ic.InsurerName,
          InsurerText:
            editingInsurers[ic.CascoConditionInsurerId] || ic.InsurerText,
        }));

      await updateInsurerMutation.mutateAsync(updatedInsurerConditions);
      setHasChanges(false);
    } catch (error) {
      console.error("Error saving changes:", error);
    }
  };

  const handleBack = () => {
    if (hasChanges) {
      setShowUnsavedChangesDialog(true);
    } else {
      navigate("/settings/casco-conditions");
    }
  };

  const handleConfirmLeave = () => {
    setShowUnsavedChangesDialog(false);
    navigate("/settings/casco-conditions");
  };

  if (isLoading) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", padding: "2rem" }}
      >
        <Spinner label={t("qtg.common.loading")} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "1rem" }}>
        <MessageBar intent="error">
          {t("qtg.settings.cascoConditions.loadError")}
        </MessageBar>
      </div>
    );
  }

  if (!condition) {
    return (
      <div style={{ padding: "1rem" }}>
        <MessageBar intent="warning">
          {t("qtg.settings.cascoConditions.conditionNotFound")}
        </MessageBar>
      </div>
    );
  }

  return (
    <div>
      <Navigation
        showCascoConditionsLink={false}
        displaySearch={false}
        displayReload={false}
        rightSideButtons={
          <div style={{ display: "flex", gap: "8px" }}>
            <Button
              appearance="secondary"
              icon={<ArrowLeftRegular />}
              onClick={handleBack}
            >
              {t("qtg.common.back")}
            </Button>
            <Button
              appearance="primary"
              icon={<Save24Regular />}
              onClick={handleSaveChanges}
              disabled={!hasChanges || updateInsurerMutation.isPending}
            >
              {updateInsurerMutation.isPending
                ? t("qtg.common.saving")
                : t("qtg.common.save")}
            </Button>
          </div>
        }
      />

      <div style={{ padding: "1rem" }}>
        <Card>
          <CardHeader
            header={
              <div>
                <Text weight="semibold" size={500}>
                  {t("qtg.settings.cascoConditions.editCondition")}
                </Text>
                <br />
                <Text
                  size={400}
                  style={{ color: "var(--colorNeutralForeground2)" }}
                >
                  {condition.Name}
                </Text>
              </div>
            }
          />
          <CardPreview style={{ padding: 0 }}>
            {hasChanges && (
              <div style={{ padding: "16px 16px 0 16px" }}>
                <MessageBar intent="info">
                  {t("qtg.settings.cascoConditions.unsavedChanges")}
                </MessageBar>
              </div>
            )}

            <div style={{ width: "100%" }}>
              {/* Table Header */}
              <div
                style={{
                  display: "flex",
                  borderBottom: "1px solid var(--colorNeutralStroke1)",
                  backgroundColor: "var(--colorNeutralBackground2)",
                  fontWeight: 600,
                  padding: "12px 16px",
                }}
              >
                <div style={{ width: "200px" }}>
                  {t("qtg.settings.cascoConditions.insurer")}
                </div>
                <div style={{ flex: 1 }}>
                  {t("qtg.settings.cascoConditions.conditionText")}
                </div>
              </div>
              {/* Table Body */}
              {condition.InsurerConditions.map((insurerCondition) => (
                <div
                  key={insurerCondition.CascoConditionInsurerId}
                  style={{
                    display: "flex",
                    borderBottom: "1px solid var(--colorNeutralStroke1)",
                    padding: "12px 16px",
                    alignItems: "flex-start",
                  }}
                >
                  <div style={{ width: "200px", paddingTop: "8px" }}>
                    <Text weight="semibold">
                      {insurerCondition.InsurerName}
                    </Text>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Textarea
                      value={
                        editingInsurers[
                          insurerCondition.CascoConditionInsurerId
                        ] || ""
                      }
                      onChange={(_, data) =>
                        handleInsurerTextChange(
                          insurerCondition.CascoConditionInsurerId,
                          data.value
                        )
                      }
                      placeholder={t(
                        "qtg.settings.cascoConditions.conditionTextPlaceholder"
                      )}
                      rows={3}
                      resize="vertical"
                      style={{ width: "100%" }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardPreview>
        </Card>
      </div>

      <ConfirmDialog
        open={showUnsavedChangesDialog}
        onClose={() => setShowUnsavedChangesDialog(false)}
        onConfirm={handleConfirmLeave}
        title={t("qtg.common.unsavedChanges", "Unsaved Changes")}
        message={t(
          "qtg.settings.cascoConditions.confirmUnsavedChanges",
          "You have unsaved changes. Are you sure you want to leave?"
        )}
        confirmText={t("qtg.common.leave", "Leave")}
        cancelText={t("qtg.common.stay", "Stay")}
      />
    </div>
  );
};
